import { Link } from 'react-router-dom'
import { Avatar } from '@/components/Avatar'
import { Badge } from '@/components/Badge'
import { Icon } from '@/components/Icon'
import { formatIdentityLine } from '@/lib/age'
import { formatDaysLate, formatVisitDate } from '@/lib/dates'
import type { FollowUp } from './api'
import { RESULT_ROW, RESULT_ROW_CHEVRON } from '@/components/ResultRow'

/**
 * One patient due back, shaped exactly like a patient and a caller result row.
 *
 * The three sit side by side on the dashboard. This one was written inline on
 * that screen instead of beside the other two, and drifted the way inline
 * copies do: no avatar, a name a size smaller, a shorter row, no chevron — and
 * no horizontal padding, so it started left of its own card title while its
 * neighbours were inset. The hover highlight ran to the card's edges with it,
 * which is what made it read as content escaping the box rather than as a row
 * styled slightly differently.
 *
 * Keep the shell identical to `PatientResultRow` and `CallerResultRow`. A
 * difference between these three is a mistake to a reader, never a distinction.
 */
export function FollowUpResultRow({ followUp }: { followUp: FollowUp }) {
  const identity = formatIdentityLine(followUp.age, followUp.sex)
  // Words, never a colour alone — readable to a colour-blind reader under
  // fluorescent light.
  const status =
    followUp.days_overdue > 0 ? formatDaysLate(followUp.days_overdue) : formatVisitDate(followUp.due_on)

  return (
    <li>
      <Link
        to={`/patients/${followUp.patient_id}`}
        className={RESULT_ROW}
        aria-label={`${followUp.full_name}, ${identity || 'details not recorded'}, OP number ${followUp.op_number}, ${
          followUp.days_overdue > 0 ? status : `due ${status}`
        }`}
      >
        <Avatar name={followUp.full_name} size={36} />
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          {/* On a phone the name and the OP number cannot both fit on one line,
              and the name is the thing being read. */}
          <span className="flex min-w-0 items-baseline gap-2 max-[30rem]:flex-wrap max-[30rem]:gap-y-0">
            <span className="truncate text-base leading-tight font-semibold">
              {followUp.full_name}
            </span>
            <span className="text-xs whitespace-nowrap text-fg-subtle tabular-nums">
              {followUp.op_number}
            </span>
          </span>
          {identity && (
            <span className="truncate text-xs leading-snug text-fg-muted">{identity}</span>
          )}
        </span>
        <Badge tone={followUp.days_overdue > 0 ? 'warning' : 'neutral'}>{status}</Badge>
        <span className={RESULT_ROW_CHEVRON}>
          <Icon name="chevronRight" size="1rem" />
        </span>
      </Link>
    </li>
  )
}
